import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { Button } from "../Components/ui/button";
import { Badge } from "../Components/ui/badge";
import { ChevronLeft, ChevronRight, Play, Loader2, ArrowLeft, Video, Volume2, VolumeX, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "../Components/ui/dialog";

export default function ImpactHighlights() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [failedUrls, setFailedUrls] = useState([]);

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['donorDashboard'],
    queryFn: async () => {
      const { data } = await apiClient.get('/users/dashboard');
      return data;
    }
  });

  const rawVideos = dashboardData?.proofVideos || [];
  const videos = rawVideos.filter(v => !failedUrls.includes(v.file_url));

  // Auto-advance slide every 8 seconds if no lightbox is open
  useEffect(() => {
    if (videos.length <= 1 || selectedVideo) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % videos.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [videos.length, selectedVideo]);

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center bg-zinc-950 text-white">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-rose-500 mx-auto" />
          <p className="text-zinc-400 font-medium animate-pulse">Loading theater...</p>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="min-h-screen p-6 bg-zinc-950 text-white flex flex-col items-center justify-center">
        <div className="max-w-md text-center space-y-6">
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-full inline-block">
            <Video className="w-12 h-12 text-zinc-600" />
          </div>
          <h2 className="text-2xl font-bold">No highlights yet</h2>
          <p className="text-zinc-400 text-sm">
            Your proof videos will appear here once your donations are scanned and processed.
          </p>
          <Button 
            onClick={() => navigate('/donordashboard')}
            className="bg-white hover:bg-zinc-200 text-zinc-950 font-semibold px-6 py-2 rounded-full transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const activePosition = activeIndex >= videos.length ? Math.max(0, videos.length - 1) : activeIndex;
  const activeVideo = videos[activePosition];

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % videos.length);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-hidden relative flex flex-col">
      {/* Ambient background glow matching the active video */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-30 blur-[120px] transition-all duration-1000">
        <div className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] rounded-full bg-gradient-to-br from-rose-600 to-amber-500 animate-pulse"></div>
        <div className="absolute -bottom-[10%] -right-[10%] w-[55%] h-[55%] rounded-full bg-gradient-to-tr from-violet-600 to-blue-500 animate-pulse delay-700"></div>
      </div>

      {/* Top Header Bar */}
      <header className="relative z-10 px-6 py-4 flex items-center justify-between border-b border-white/5 bg-zinc-900/50 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/donordashboard')}
            className="text-zinc-400 hover:text-white hover:bg-white/10 rounded-full h-10 w-10 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-extrabold tracking-tight bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent">
              IMPACT THEATER
            </h1>
            <p className="text-xs text-zinc-500">Your direct contribution updates in motion</p>
          </div>
        </div>
        <Badge className="bg-rose-500/10 border-rose-500/20 text-rose-400 uppercase tracking-widest font-mono text-[10px] px-3 py-1">
          {activePosition + 1} / {videos.length} Clip{videos.length > 1 ? 's' : ''}
        </Badge>
      </header>

      {/* Main Theater Display */}
      <main className="flex-1 relative z-10 max-w-6xl w-full mx-auto p-6 md:p-8 flex flex-col justify-center items-center gap-8">
        
        {/* Large Stage Carousel */}
        <div className="relative w-full aspect-video md:max-h-[550px] bg-zinc-900/40 border border-white/10 rounded-3xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] backdrop-blur-md group">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePosition}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              {/* Theater Media Display */}
              {activeVideo.proof_type === 'photo' ? (
                <img
                  src={activeVideo.file_url}
                  className="w-full h-full object-cover"
                  alt="Impact proof"
                  onError={() => setFailedUrls((prev) => [...prev, activeVideo.file_url])}
                />
              ) : (
                <video
                  src={activeVideo.file_url}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted={isMuted}
                  playsInline
                  loop
                  onError={(e) => {
                    console.warn("Video failed to load in ImpactHighlights main player:", activeVideo.file_url);
                    if (activeVideo.file_url && activeVideo.file_url.includes('amazonaws.com')) {
                      e.target.src = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
                      e.target.load();
                      e.target.play().catch(err => console.log("Autoplay blocked on fallback:", err.message));
                    } else {
                      setFailedUrls((prev) => [...prev, activeVideo.file_url]);
                    }
                  }}
                />
              )}

              {/* Gradient Dark Overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30 pointer-events-none" />

              {/* Interactive Controls Overlay */}
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 z-20">
                <div className="space-y-2 max-w-lg">
                  <Badge className="bg-white/20 hover:bg-white/30 text-white backdrop-blur-md border border-white/10 capitalize px-3 py-1 text-xs">
                    {activeVideo.donation_id?.donation_type || 'Donation'} Impact Proof
                  </Badge>
                </div>

                 <div className="flex items-center gap-3">
                  {/* Mute toggle button - only for videos */}
                  {activeVideo.proof_type !== 'photo' && (
                    <Button
                      variant="outline"
                      onClick={() => setIsMuted(!isMuted)}
                      className="h-12 w-12 rounded-full border-white/15 bg-black/45 hover:bg-black/60 hover:border-white/35 text-white flex items-center justify-center shadow-lg"
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </Button>
                  )}

                  {/* Play Fullscreen Lightbox Button */}
                  <Button
                    onClick={() => setSelectedVideo(activeVideo)}
                    className="h-12 px-6 rounded-full bg-rose-600 hover:bg-rose-500 text-white font-bold flex items-center gap-2 shadow-lg hover:shadow-rose-600/20 hover:-translate-y-0.5 transition-all"
                  >
                    {activeVideo.proof_type === 'photo' ? (
                      <>
                        <Eye className="w-4 h-4" />
                        Expand Photo
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 fill-white" />
                        Expand Clip
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Left Navigation Arrow */}
          {videos.length > 1 && (
            <Button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border-white/10 bg-black/30 hover:bg-black/70 text-white hidden group-hover:flex items-center justify-center backdrop-blur-md z-30 transition-all border"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
          )}

          {/* Right Navigation Arrow */}
          {videos.length > 1 && (
            <Button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border-white/10 bg-black/30 hover:bg-black/70 text-white hidden group-hover:flex items-center justify-center backdrop-blur-md z-30 transition-all border"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          )}
        </div>

        {/* Carousel Indicators / Thumbnails Bar */}
        {videos.length > 1 && (
          <div className="flex justify-center items-center gap-3 py-2 z-10 w-full overflow-x-auto">
            {videos.map((vid, idx) => (
              <button
                key={vid._id}
                onClick={() => setActiveIndex(idx)}
                className={`relative h-14 w-24 rounded-lg overflow-hidden border-2 transition-all duration-300 flex-shrink-0 ${
                  activePosition === idx ? "border-rose-500 scale-105 shadow-md shadow-rose-500/10" : "border-white/10 opacity-60 hover:opacity-100"
                }`}
              >
                {vid.proof_type === 'photo' ? (
                  <img 
                    src={vid.file_url} 
                    className="w-full h-full object-cover pointer-events-none" 
                    alt="Thumbnail" 
                    onError={() => setFailedUrls((prev) => [...prev, vid.file_url])}
                  />
                ) : (
                  <video 
                    src={vid.file_url} 
                    className="w-full h-full object-cover pointer-events-none" 
                    muted 
                    onError={(e) => {
                      if (vid.file_url && vid.file_url.includes('amazonaws.com')) {
                        e.target.src = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
                      } else {
                        setFailedUrls((prev) => [...prev, vid.file_url]);
                      }
                    }}
                  />
                )}
                <div className={`absolute inset-0 ${activePosition === idx ? "bg-black/0" : "bg-black/40"}`} />
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Cinematic Fullscreen Lightbox Player */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-5xl bg-zinc-950 border-white/10 p-0 overflow-hidden shadow-2xl">
          {selectedVideo && (
            <div className="relative aspect-video w-full bg-black flex items-center justify-center">
              {selectedVideo.proof_type === 'photo' ? (
                <img
                  src={selectedVideo.file_url}
                  className="max-h-full max-w-full object-contain"
                  alt="Expanded proof"
                />
              ) : (
                <video
                  src={selectedVideo.file_url}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    if (selectedVideo.file_url && selectedVideo.file_url.includes('amazonaws.com')) {
                      e.target.src = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
                      e.target.load();
                      e.target.play().catch(err => console.log("Autoplay blocked on fallback:", err.message));
                    }
                  }}
                />
              )}
              <div className="absolute bottom-4 left-4 right-4 bg-gradient-to-t from-black/80 p-4 rounded-xl pointer-events-none">
                <Badge className="bg-rose-500 text-white text-[10px] uppercase font-mono tracking-widest mb-1">
                  Theater Expand
                </Badge>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
